import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Calendar, MapPin, Users, ImagePlus, X } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function Events() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [events, setEvents] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const [regs, setRegs] = useState(null);
  const [f, setF] = useState({ title: "", description: "", event_date: "", location: "", lga: "", capacity: "", image_path: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(() => { setLoading(true); api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); api.get("/structure/lgas").then((r) => setLgas(r.data.lgas)).catch(() => {}); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/feeds/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_path", data.image_path); toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPreview(""); }
    finally { setUploading(false); }
  };

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/events", {
        title: f.title, description: f.description, event_date: f.event_date, location: f.location,
        lga: isSuper ? (f.lga || undefined) : undefined, capacity: f.capacity ? parseInt(f.capacity, 10) : undefined,
        image_path: f.image_path || undefined,
      });
      toast.success("Event created");
      setOpen(false); setF({ title: "", description: "", event_date: "", location: "", lga: "", capacity: "", image_path: "" }); setPreview(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/events/${id}`); toast.success("Event deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewRegs = async (ev) => {
    try { const { data } = await api.get(`/admin/events/${ev.id}/registrations`); setRegs({ event: ev, list: data }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const valid = f.title && f.description && f.event_date && f.location && !uploading;

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-heading font-black text-3xl">Events & Programs</h1>
          <p className="text-muted-foreground">{isSuper ? "Create events for any LGA." : `Events for ${user?.assigned_lga} LGA.`}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-event-btn" className="rounded-full bg-primary font-bold gap-2"><Plus className="w-4 h-4" /> New Event</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Event</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="mb-1.5 block text-sm font-semibold">Title</Label><Input data-testid="event-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Keffi Mega Rally" /></div>
              <div><Label className="mb-1.5 block text-sm font-semibold">Description</Label><Textarea data-testid="event-desc" value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="What is this event about?" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block text-sm font-semibold">Date & Time</Label><Input data-testid="event-date" type="datetime-local" value={f.event_date} onChange={(e) => set("event_date", e.target.value)} /></div>
                <div><Label className="mb-1.5 block text-sm font-semibold">Capacity (optional)</Label><Input data-testid="event-capacity" type="number" value={f.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="e.g. 500" /></div>
              </div>
              <div><Label className="mb-1.5 block text-sm font-semibold">Location</Label><Input data-testid="event-location" value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Lafia Township Stadium" /></div>
              {isSuper && (
                <div><Label className="mb-1.5 block text-sm font-semibold">LGA (optional)</Label>
                  <Select value={f.lga} onValueChange={(v) => set("lga", v)}>
                    <SelectTrigger data-testid="event-lga"><SelectValue placeholder="Statewide / select LGA" /></SelectTrigger>
                    <SelectContent>{lgas.map((l) => <SelectItem key={l.code} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="mb-1.5 block text-sm font-semibold">Banner (optional)</Label>
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-5 cursor-pointer hover:border-primary transition-colors text-sm text-muted-foreground">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}{uploading ? "Uploading..." : "Choose image"}
                  <input data-testid="event-image" type="file" accept="image/*" className="hidden" onChange={upload} />
                </label>
                {preview && <img src={preview} alt="preview" className="mt-2 rounded-xl w-full aspect-video object-cover" />}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="event-create" onClick={create} disabled={!valid || saving} className="bg-primary gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />}Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
        : events.length === 0 ? <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center text-muted-foreground"><Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />No events yet.</div>
        : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((e) => (
              <div key={e.id} data-testid={`admin-event-${e.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                {e.image_path && <img src={`${API}/files/${e.image_path}`} alt={e.title} className="w-full aspect-video object-cover" />}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading font-bold text-lg">{e.title}</h3>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button data-testid={`event-delete-${e.id}`} size="icon" variant="ghost" className="text-accent hover:bg-red-50 shrink-0"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete "{e.title}"?</AlertDialogTitle><AlertDialogDescription>This event and all its registrations will be removed.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(e.id)} className="bg-accent">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  {e.lga && <span className="text-[11px] font-bold uppercase bg-primary/10 text-primary px-2 py-0.5 rounded-full">{e.lga}</span>}
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" /> {new Date(e.event_date).toLocaleString("en-NG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> {e.location}</div>
                  </div>
                  <Button data-testid={`event-view-regs-${e.id}`} variant="outline" onClick={() => viewRegs(e)} className="w-full mt-3 rounded-full border-2 border-primary text-primary font-bold gap-2"><Users className="w-4 h-4" /> {e.registered} Registered</Button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={!!regs} onOpenChange={(o) => !o && setRegs(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Registrations — {regs?.event.title}</DialogTitle></DialogHeader>
          {regs && (regs.list.length === 0 ? <p className="text-muted-foreground text-sm py-6 text-center">No registrations yet.</p> : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="py-2">Name</th><th className="py-2">Phone</th><th className="py-2">LGA</th></tr></thead>
              <tbody>{regs.list.map((r) => <tr key={r.id} className="border-t border-slate-50"><td className="py-2 font-medium">{r.name}</td><td className="py-2">{r.phone}</td><td className="py-2 text-muted-foreground">{r.lga || "—"}</td></tr>)}</tbody>
            </table>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
